import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Badge from '@divvy-web/skylab.badge'
import { BASIC_BUTTON_TYPE_FLAT } from '@divvy-web/skylab.basicbutton'
import Icon from '@divvy-web/skylab.icon'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'

import CustomerAssistanceButton from '@sputnik/components/CustomerAssistanceButton/CustomerAssistanceButton'
import GoToDashboardButton from '@sputnik/components/GoToDashboardButton'
import BillSideBarIcon from '@sputnik/components/legacy/BillSideBarIcon'
import useDeviceDetect from '@sputnik/hooks/useDeviceDetect'
import useDivvyUUID from '@sputnik/hooks/useDivvyUUID'
import GetDecisions from '@sputnik/pages/gql/GetDecisions.gql'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import {
  getOfferType,
  SECURED_LINE_OFFER,
  STANDARD_LINE_OFFER,
  VARIABLE_LINE_OFFER,
} from '@sputnik/utils/underwritingUtils'

import OfferInfoMobilePopSheet from './OfferInfoMobilePopSheet'
import OfferTile from './OfferTile/OfferTile'
import { mobileSuccessPreApprovedOffersCss, webSuccessPreApprovedOffersCss } from './successPreApprovedOffersStyles'

const SuccessPreApprovedOffers = () => {
  const navigate = useNavigate()
  const [companyDivvyUuid] = useDivvyUUID()
  const { isMobile } = useDeviceDetect()
  const [isShowingStandardOfferPopSheet, setIsShowingStandardOfferPopSheet] = useState(false)
  const [isShowingVariableOfferPopSheet, setIsShowingVariableOfferPopSheet] = useState(false)
  const [isShowingSecuredOfferPopSheet, setIsShowingSecuredOfferPopSheet] = useState(false)
  const [getClassName, makeTestId] = useNameFormatter('SuccessPreApprovedOffers')

  const { data: decisionsData } = useQuery(GetDecisions, {
    fetchPolicy: 'network-only',
    skip: !companyDivvyUuid,
    variables: { companyId: companyDivvyUuid },
  })

  const decisions = decisionsData?.decisions || []

  const standardLineOffer = getOfferType(decisions, STANDARD_LINE_OFFER)
  const variableLineOffer = getOfferType(decisions, VARIABLE_LINE_OFFER)
  const securedLineOffer = getOfferType(decisions, SECURED_LINE_OFFER)

  const hasStandardOffer = standardLineOffer != null
  const hasVariableOffer = variableLineOffer != null
  const hasSecuredOffer = securedLineOffer != null

  const hasThreeOffers = hasStandardOffer && hasVariableOffer && hasSecuredOffer
  const offerCount = hasThreeOffers ? 3 : 2

  const standardApprovedAmount = standardLineOffer?.approvedAmountInCents
  const variableApprovedAmount = variableLineOffer?.approvedAmountInCents

  const toggleStandardOfferPopSheet = () => {
    setIsShowingStandardOfferPopSheet(!isShowingStandardOfferPopSheet)
  }

  const toggleVariableOfferPopSheet = () => {
    setIsShowingVariableOfferPopSheet(!isShowingVariableOfferPopSheet)
  }

  const toggleSecuredOfferPopSheet = () => {
    setIsShowingSecuredOfferPopSheet(!isShowingSecuredOfferPopSheet)
  }

  const navigateToStandardCreditLineOffer = () => {
    navigate(PATHNAME_CONSTS.STANDARD_OFFER_PATH)
  }

  const navigateToVariableCreditLineOffer = () => {
    navigate(PATHNAME_CONSTS.VARIABLE_OFFER_PATH)
  }

  const navigateToSecuredCreditLineOffer = () => {
    navigate(PATHNAME_CONSTS.SECURED_OFFER_PATH)
  }

  const standardOfferTopCaption = (
    <FormattedMessage
      defaultMessage='Your credit limit is fixed and typically won’t change.'
      id='sputnik.StandardCreditLineOfferPopSheet__kT0CVZ'
    />
  )

  const standardOfferBottomCaption = (
    <FormattedMessage
      defaultMessage='Great for companies that require a steady credit limit from month to month.'
      id='sputnik.SuccessPreApprovedOffers__YFvyE/'
    />
  )

  const variableOfferTopCaption = (
    <FormattedMessage
      defaultMessage='Your credit limit may adjust based on your bank account balance.'
      id='sputnik.SuccessPreApprovedOffers__1hE+fo'
    />
  )

  const variableOfferBottomCaption = (
    <FormattedMessage
      defaultMessage='Ideal for companies that need a flexible credit limit that grows with their business.'
      id='sputnik.SuccessPreApprovedOffers__+j1YHY'
    />
  )

  const securedOfferBottomCaption = (
    <FormattedMessage
      defaultMessage='Credit limit is funded by your own cash deposit, which you can adjust at any time.'
      id='sputnik.SuccessPreApprovedOffers__IvzXNC'
    />
  )

  return (
    <div
      css={isMobile ? mobileSuccessPreApprovedOffersCss : webSuccessPreApprovedOffersCss}
      data-testid={makeTestId('')}
    >
      <CustomerAssistanceButton />
      <div className='main-container fs-unmask'>
        <header className={getClassName('header-content')}>
          <section className={getClassName('title-and-subtitle')}>
            <div className={getClassName('title')}>
              <FormattedMessage
                defaultMessage='Congrats! You pre-qualify for {offerCount} offers.'
                id='sputnik.SuccessPreApprovedOffers__8YMraL'
                values={{ offerCount }}
              />
            </div>
            <div className={getClassName('subtitle')}>
              <FormattedMessage
                defaultMessage='Based on the information from your credit application, you’re eligible for the following credit offers:'
                id='sputnik.SuccessPreApprovedOffers__et15an'
              />
            </div>
          </section>
          {!isMobile && (
            <section>
              <GoToDashboardButton
                buttonType={BASIC_BUTTON_TYPE_FLAT}
                componentName={makeTestId('')}
              />
            </section>
          )}
          {!isMobile && (
            <div className={getClassName('bill-logo')}>
              <BillSideBarIcon />
            </div>
          )}
        </header>
        <main className={getClassName('offer-info-and-tiles')}>
          <div className={getClassName('offer-info')}>
            <p className={getClassName('offer-info-list-title')}>
              <FormattedMessage
                defaultMessage='Included with all offers:'
                id='sputnik.SuccessPreApprovedOffers__MJHQ84'
              />
            </p>
            <ul
              className={getClassName('offer-info-list')}
              data-testid={makeTestId('offer-info-list')}
            >
              <li className={getClassName('offer-info-list-item')}>
                <FormattedMessage
                  defaultMessage='Earn rewards as you spend'
                  id='sputnik.SuccessPreApprovedOffers__g+2llw'
                />
              </li>
              <li className={getClassName('offer-info-list-item')}>
                <FormattedMessage
                  defaultMessage='Free access to BILL Spend & Expense platform'
                  id='sputnik.SuccessPreApprovedOffers__g4bZtX'
                />
              </li>
              <li className={getClassName('offer-info-list-item')}>
                <FormattedMessage
                  defaultMessage='Pay zero interest fees'
                  id='sputnik.SuccessPreApprovedOffers__S4fbfk'
                />
              </li>
            </ul>
          </div>
          {/* Standard offer tiles */}
          {hasStandardOffer && (
            <OfferTile
              approvedAmount={standardApprovedAmount}
              bottomCaption={isMobile ? null : standardOfferBottomCaption}
              dataTestId={makeTestId('standard-offer-tile')}
              image='spot-standard-credit-line'
              infoChip={
                <Badge>
                  <Icon name='ai-insight' />
                  <FormattedMessage
                    defaultMessage='The OG'
                    id='sputnik.SuccessPreApprovedOffers__S9fbpp'
                  />
                </Badge>
              }
              title={
                <FormattedMessage
                  defaultMessage='Standard credit line'
                  id='sputnik.SuccessPreApprovedOffers__WQCeHH'
                />
              }
              topCaption={standardOfferTopCaption}
              onTileClick={isMobile ? toggleStandardOfferPopSheet : navigateToStandardCreditLineOffer}
            />
          )}
          {/* Variable offer tiles */}
          {hasVariableOffer && (
            <OfferTile
              approvedAmount={variableApprovedAmount}
              bottomCaption={isMobile ? null : variableOfferBottomCaption}
              dataTestId={makeTestId('variable-offer-tile')}
              image='spot-variable-credit-line'
              infoChip={
                <Badge>
                  <Icon name='reports' />
                  <FormattedMessage
                    defaultMessage='The growth engine'
                    id='sputnik.SuccessPreApprovedOffers__S94b4a'
                  />
                </Badge>
              }
              title={
                <FormattedMessage
                  defaultMessage='Variable credit line'
                  id='sputnik.SuccessPreApprovedOffers__8ugHZu'
                />
              }
              topCaption={variableOfferTopCaption}
              onTileClick={isMobile ? toggleVariableOfferPopSheet : navigateToVariableCreditLineOffer}
            />
          )}
          {/* Secured offer tiles */}
          {hasSecuredOffer && (
            <OfferTile
              approvedAmountPlaceHolder={
                <FormattedMessage
                  defaultMessage='You choose the credit limit'
                  id='sputnik.SuccessPreApprovedOffers__9QCeLo'
                />
              }
              bottomCaption={securedOfferBottomCaption}
              dataTestId={makeTestId('secured-offer-tile')}
              image='spot-secured-credit-line'
              infoChip={
                <Badge>
                  <Icon name='finance-tools' />
                  <FormattedMessage
                    defaultMessage='The rewards machine'
                    id='sputnik.SuccessPreApprovedOffers__4Bz14c'
                  />
                </Badge>
              }
              title={
                <FormattedMessage
                  defaultMessage='Prepayment'
                  id='sputnik.SuccessPreApprovedOffers__Zm8wJX'
                />
              }
              topCaption={isMobile ? securedOfferBottomCaption : ''}
              onTileClick={isMobile ? toggleSecuredOfferPopSheet : navigateToSecuredCreditLineOffer}
            />
          )}
          {isMobile && (
            <TextLink
              className={getClassName('navigate-to-dashboard')}
              dataTestId={makeTestId('navigate-to-dashboard')}
              size={TextLinkSizes.REGULAR}
              onClick={() => navigate(PATHNAME_CONSTS.DASHBOARD_PATH)}
            >
              <FormattedMessage
                defaultMessage='Return to dashboard'
                id='sputnik.SuccessPreApprovedOffers__Awhp8T'
              />
            </TextLink>
          )}
        </main>
      </div>
      {isMobile && (
        <>
          {/* Standard offer mobile popsheet */}
          <OfferInfoMobilePopSheet
            bottomCaption={standardOfferBottomCaption}
            image='spot-standard-credit-line'
            isShowing={isShowingStandardOfferPopSheet}
            navigateToSelectedOffer={navigateToStandardCreditLineOffer}
            subtitle={
              <FormattedMessage
                defaultMessage="What is a 'standard credit line'?"
                id='sputnik.SuccessPreApprovedOffers__9RNpI8'
              />
            }
            togglePopSheet={toggleStandardOfferPopSheet}
            topCaption={standardOfferTopCaption}
          />
          {/* Variable offer mobile popsheet */}
          <OfferInfoMobilePopSheet
            bottomCaption={variableOfferBottomCaption}
            image='spot-variable-credit-line'
            isShowing={isShowingVariableOfferPopSheet}
            navigateToSelectedOffer={navigateToVariableCreditLineOffer}
            subtitle={
              <FormattedMessage
                defaultMessage="What is a 'variable credit line'?"
                id='sputnik.SuccessPreApprovedOffers__x83Whl'
              />
            }
            togglePopSheet={toggleVariableOfferPopSheet}
            topCaption={variableOfferTopCaption}
          />
          {/* Secured offer mobile popsheet */}
          {/* @ts-expect-error - this code is going to be deleted when v2 is gone, no point fixing */}
          <OfferInfoMobilePopSheet
            bottomCaption={securedOfferBottomCaption}
            image='spot-secured-credit-line'
            isShowing={isShowingSecuredOfferPopSheet}
            navigateToSelectedOffer={navigateToSecuredCreditLineOffer}
            subtitle={
              <FormattedMessage
                defaultMessage="What is 'Prepayment'?"
                id='sputnik.SuccessPreApprovedOffers__N1HW4b'
              />
            }
            togglePopSheet={toggleSecuredOfferPopSheet}
          />
        </>
      )}
    </div>
  )
}
export default SuccessPreApprovedOffers
